import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Rocket, Sparkles, Pencil, Trash2, Languages, CheckCircle2 } from 'lucide-react';
import { api } from '../../lib/api';
import Breadcrumb from '../../components/Breadcrumb';

interface ChangelogEntry {
  chapterId: string;
  type: 'added' | 'modified' | 'deleted';
  titleCs: string;
}

interface TranslationStatus {
  total: number;
  translated: number;
  manual: number;
  missing: number;
}

export default function AdminPublish() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'publish-preview'],
    queryFn: () => api.get<{ changelog: ChangelogEntry[] }>('/versions/preview'),
  });

  const [releaseNote, setReleaseNote] = useState('');
  const [sendEmails, setSendEmails] = useState(true);
  const qc = useQueryClient();
  const publish = useMutation({
    mutationFn: () =>
      api.post('/versions/publish', { release_note: releaseNote, send_emails: sendEmails }),
    onSuccess: () => {
      qc.invalidateQueries();
      refetch();
      setReleaseNote('');
    },
  });

  const changelog = data?.changelog ?? [];
  const added = changelog.filter((c) => c.type === 'added').length;
  const modified = changelog.filter((c) => c.type === 'modified').length;
  const deleted = changelog.filter((c) => c.type === 'deleted').length;

  return (
    <div>
      <Breadcrumb items={[{ label: 'Admin' }, { label: 'Publikovat verzi' }]} />
      <h1 className="mb-8 font-heading text-3xl font-bold text-dark">Publikovat novou verzi</h1>

      {isLoading ? (
        <div className="text-sm text-gray-500">Načítám…</div>
      ) : changelog.length === 0 ? (
        <div className="card p-12 text-center text-gray-500">
          Žádné neuložené změny — nemáš co publikovat.
        </div>
      ) : (
        <>
          <div className="mb-6 flex gap-4">
            <StatCard label="Nové" value={added} color="teal" />
            <StatCard label="Upravené" value={modified} color="gold" />
            <StatCard label="Smazané" value={deleted} color="danger" />
          </div>

          <div className="card mb-6 overflow-hidden">
            <div className="border-b border-gray-100 bg-gray-50 px-5 py-3 text-xs font-bold uppercase tracking-wide text-gray-600">
              Seznam změn
            </div>
            <ul>
              {changelog.map((c) => (
                <li
                  key={c.chapterId}
                  className="flex items-center gap-3 border-b border-gray-100 px-5 py-3 text-sm last:border-b-0"
                >
                  {c.type === 'added' && <Sparkles className="h-4 w-4 text-teal" />}
                  {c.type === 'modified' && <Pencil className="h-4 w-4 text-gold" />}
                  {c.type === 'deleted' && <Trash2 className="h-4 w-4 text-danger" />}
                  <span className="flex-1">{c.titleCs}</span>
                  <span className="text-xs uppercase tracking-wide text-gray-500">{c.type}</span>
                </li>
              ))}
            </ul>
          </div>

          <label className="label">Release note (volitelné)</label>
          <textarea
            value={releaseNote}
            onChange={(e) => setReleaseNote(e.target.value)}
            className="input mb-6 h-28"
            placeholder="Co je nového v této verzi…"
          />

          <label className="mb-5 flex cursor-pointer items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={sendEmails}
              onChange={(e) => setSendEmails(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-teal focus:ring-teal"
            />
            Odeslat email notifikaci uživatelům se změněnými kapitolami
          </label>

          <button
            onClick={() => publish.mutate()}
            disabled={publish.isPending}
            className="btn-primary"
          >
            <Rocket className="h-4 w-4" />
            {publish.isPending ? 'Publikuji…' : 'Publikovat verzi'}
          </button>
        </>
      )}

      <TranslationPanel />
    </div>
  );
}

function TranslationPanel() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin', 'translation-status'],
    queryFn: () => api.get<TranslationStatus>('/admin/translation-status'),
    refetchInterval: 5000,
  });

  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runBatch = async () => {
    setRunning(true);
    setError(null);
    try {
      // Smyčka: každé volání přeloží jen krok bloků jedné kapitoly (kvůli limitu
      // Pages Functions — celá kapitola s max_tokens=8192 nestreamovaně přeteče a
      // Cloudflare vrátí HTML 502). Server drží progres v block-cache, my jen
      // smyčkujeme, dokud něco chybí. `progressed=false` je pojistka proti zacyklení.
      while (true) {
        const res = await api.post<{ remaining: number; progressed: boolean }>(
          '/admin/translate-missing',
        );
        await refetch();
        if (res.remaining === 0 || !res.progressed) break;
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setRunning(false);
    }
  };

  if (isLoading || !data) {
    return null;
  }

  const ratio = data.total > 0 ? (data.translated / data.total) * 100 : 0;
  const allDone = data.missing === 0;

  return (
    <div className="card mt-10 p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Languages className="h-4 w-4 text-teal" />
          <h3 className="font-heading text-lg font-bold text-dark">Anglický překlad</h3>
        </div>
        {allDone ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-positive-bg px-2 py-1 text-[11px] font-semibold text-positive">
            <CheckCircle2 className="h-3 w-3" />
            Vše přeloženo
          </span>
        ) : (
          <button
            onClick={runBatch}
            disabled={running}
            className="btn-primary"
            title="Přeloží chybějící kapitoly po dávkách 5 (každá dávka ~30s, smyčka pokračuje až do dokončení)"
          >
            <Languages className="h-4 w-4" />
            {running ? `Překládám… (${data.missing} chybí)` : `Přeložit chybějící (${data.missing})`}
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="font-mono text-2xl font-bold text-positive">{data.translated}</div>
          <div className="text-xs uppercase tracking-wide text-gray-500">Přeloženo</div>
        </div>
        <div>
          <div className="font-mono text-2xl font-bold text-gold">{data.missing}</div>
          <div className="text-xs uppercase tracking-wide text-gray-500">Chybí</div>
        </div>
        <div>
          <div className="font-mono text-2xl font-bold text-gray-500">{data.manual}</div>
          <div className="text-xs uppercase tracking-wide text-gray-500">Ruční úprava</div>
        </div>
      </div>

      <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
        <div className="h-full bg-positive transition-all" style={{ width: `${ratio}%` }} />
      </div>

      {error && (
        <div className="mt-3 rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-danger">
          {error}
        </div>
      )}

      <p className="mt-3 text-xs text-gray-500">
        Auto-překlad běží i při publish, ale Cloudflare Pages Functions mají limit na request — u
        většího publish nestihne všech 60+ kapitol. Tímhle tlačítkem doplníš zbytek; překládá se po
        jedné kapitole (limit requestu), takže u hodně kapitol to chvíli trvá — nech to doběhnout.
      </p>
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: 'teal' | 'gold' | 'danger' }) {
  const bg = { teal: 'bg-teal-light text-teal', gold: 'bg-gold-soft text-gold', danger: 'bg-danger/10 text-danger' }[color];
  return (
    <div className={`flex-1 rounded-lg p-4 ${bg}`}>
      <div className="font-heading text-3xl font-bold">{value}</div>
      <div className="text-xs uppercase tracking-wide">{label}</div>
    </div>
  );
}
